import { useState, useEffect, useRef, useCallback } from "react";
import { Dropdown } from "react-bootstrap";
import IconButton from "@/components/ui/IconButton";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Axios from "@/shared/api/axios";
import StatusModal from "@/components/ui/StatusModal";
import { localizeField } from "@/shared/utils/localizeField";
import RelativeTime from '@/components/shared/RelativeTime';
import {
  fetchNotifications,
  markAsRead,
  markAllAsRead,
} from "@/shared/store/slices/notificationSlice";

// source_type 對應 icon 和顏色
const NOTIFICATION_STYLE_MAP = {
  subscription: { icon: "gem", bgClass: "bg-blue" },
  unlock_post: { icon: "unlock-keyhole", bgClass: "bg-success" },
  comment_tip: { icon: "circle-dollar-to-slot", bgClass: "bg-orange" },
  message_tip: { icon: "circle-dollar-to-slot", bgClass: "bg-orange" },
  deposit: { icon: "wallet", bgClass: "bg-warning" },
  withdrawal_success: { icon: "wallet", bgClass: "bg-warning" },
  withdrawal_failed: { icon: "wallet", bgClass: "bg-warning" },
};

// source_type 對應導向路徑（一律用 source_uuid，對應 /idol/:idolUuid、/post/:postUuid、/chats/:idolUuid）
const getNavigatePath = (sourceType, sourceUuid) => {
  switch (sourceType) {
    case "subscription":
      return `/idol/${sourceUuid}`;
    case "unlock_post":
    case "comment_tip":
      return `/post/${sourceUuid}`;
    case "message_tip":
      return `/chats/${sourceUuid}`;
    case "deposit":
    case "withdrawal_success":
    case "withdrawal_failed":
      return "/wallet";
    default:
      return null;
  }
};

function NotificationDropdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const statusModalRef = useRef();
  const [currentStatusCode, setCurrentStatusCode] = useState("");
  const token = useSelector((state) => state.user.token);
  const idolId = useSelector((state) => state.user.idolId);
  const {
    notifications,
    unreadCount,
    page,
    hasMore,
    isLoading,
  } = useSelector((state) => state.notification);

  const [activeTab, setActiveTab] = useState("all"); // all | unread
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false); // 區分載入更多
  const listRef = useRef(null);

  const displayCount = unreadCount > 99 ? "99+" : unreadCount;

  const filteredNotifications =
    activeTab === "unread"
      ? notifications.filter((n) => !n.is_read)
      : notifications;

  // 處理錯誤（axios 攔截器保證 error.httpErrorCode 必定存在）
  const handleError = (error) => {
    setCurrentStatusCode(error.httpErrorCode);
    statusModalRef.current?.show();
  };

  // 取得通知列表（統一走 notificationSlice 的 fetchNotifications thunk）
  const LIMIT = 10;
  const loadNotifications = useCallback(
    async (pageNum = 1, isLoadMore = false) => {
      if (!token || !idolId || isLoading) return;

      if (isLoadMore) setIsLoadingMore(true);

      try {
        await dispatch(
          fetchNotifications({ page: pageNum, limit: LIMIT, isLoadMore })
        ).unwrap();
      } catch (statusCode) {
        // 非字串＝拿不到狀態碼。RTK 在 rejectWithValue(undefined) 時 meta.rejectedWithValue
        // 是 !!payload = false，unwrap() 會改丟 serializeError 的 { message: 'Rejected' } 物件；
        // 讓它流進 StatusModal 會在 getErrorMessageKey 對物件呼叫 toLowerCase 而整棵樹崩掉
        if (typeof statusCode !== 'string') {
          console.error('Fetch notifications failed without status code', statusCode);
        } else {
          setCurrentStatusCode(statusCode);
          statusModalRef.current?.show();
        }
      } finally {
        setIsLoadingMore(false);
      }
    },
    [token, idolId, isLoading, dispatch]
  );

  // Dropdown 開啟時載入通知
  useEffect(() => {
    if (isOpen && token) {
      loadNotifications(1, false);
    }
  }, [isOpen, token]);

  // 無限滾動
  const handleScroll = useCallback(() => {
    if (!listRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const distance = scrollHeight - (scrollTop + clientHeight);

    if (distance < 100) {
      loadNotifications(page + 1, true);
    }
  }, [isLoading, hasMore, page, loadNotifications]);

  // 全部已讀
  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    if (!token || unreadCount === 0) return;

    try {
      const response = await Axios.post("/notify/read-all", { idol_id: idolId });

      if (response.data.status_code === "SYSTEM_1000") {
        dispatch(markAllAsRead());
      } else {
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      handleError(error);
    }
  };

  // 點擊通知：標記已讀 + 導向
  const handleNotificationClick = async (notification) => {
    // 標記已讀
    if (!notification.is_read && token) {
      try {
        const response = await Axios.post("/notify/read", {
          notification_id: notification.id,
          idol_id: idolId,
        });

        if (response.data.status_code === "SYSTEM_1000") {
          dispatch(markAsRead(notification.id));
        } else {
          setCurrentStatusCode(response.data.status_code);
          statusModalRef.current?.show();
        }
      } catch (error) {
        handleError(error);
      }
    }

    // 導向
    const path = getNavigatePath(notification.source_type, notification.source_uuid);
    if (path) {
      navigate(path);
    }
  };

  // 取得 icon
  const getIcon = (sourceType) => {
    const style = NOTIFICATION_STYLE_MAP[sourceType] || { icon: "bell", bgClass: "bg-secondary" };

    return (
      <div
        className={`notification-icon-circle flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center ${style.bgClass}`}
      >
        <FontAwesomeIcon icon={["fas", style.icon]} className="text-white" />
      </div>
    );
  };

  return (
    <>
      <Dropdown className="nav-notification" onToggle={(nextShow) => setIsOpen(nextShow)}>
        <Dropdown.Toggle
          as="button"
          type="button"
          className="nav-link border-0 bg-transparent p-0"
          id="notificationDropdown"
          aria-label={t('notifications')}
        >
          <IconButton size="lg" className="bg-white position-relative" tooltip={t('notifications')} placement="bottom" badge={unreadCount > 0 && <span className="nav-utilities-status">{displayCount}</span>}>
            <FontAwesomeIcon icon={["fas", "bell"]} />
          </IconButton>
        </Dropdown.Toggle>
        <Dropdown.Menu>
        {/* Header: Tabs + 全部已讀 */}
        <div className="d-flex justify-content-between align-items-center mb-3 px-3">
          <div className="d-flex gap-3">
            <span
              className={`pointer px-3 py-1 rounded-pill ${
                activeTab === "all" ? "bg-primary text-white" : "bg-light-gray"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab("all");
              }}
            >
              {t('all')}
            </span>
            <span
              className={`pointer px-3 py-1 rounded-pill ${
                activeTab === "unread" ? "bg-primary text-white" : "bg-light-gray"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setActiveTab("unread");
              }}
            >
              {t('unread')}
            </span>
          </div>
          <span
            className="pointer text-gray hover-p"
            onClick={handleMarkAllRead}
          >
            {t('mark_all_read')}
          </span>
        </div>

        {/* Notification List */}
        <div
          className="notification-list"
          ref={listRef}
          onScroll={handleScroll}
        >
          {filteredNotifications.length === 0 ? (
            <p className="text-center py-3 mb-0">{t('no_notifications')}</p>
          ) : (
            filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`d-flex align-items-center gap-3 px-3 py-2 pointer ${
                  !notification.is_read ? "bg-light-gray" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex-shrink-0">
                  {getIcon(notification.source_type)}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <p className="mb-0 fs-14">{localizeField(notification.content)}</p>
                  <small className="text-gray fs-12">
                    <RelativeTime value={notification.created_at} />
                  </small>
                </div>
              </div>
            ))
          )}
          {isLoadingMore && (
            <div className="text-center py-3">
              <span className="loading-spinner"></span>
            </div>
          )}
        </div>
        </Dropdown.Menu>
      </Dropdown>

      {/* Status Modal */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
}

export default NotificationDropdown;
